import RemoteApi from './BaseRemoteApi'
import {
  ARMApplication,
  ARMApplicationDetail,
  ARMComponent,
  PutARMApplicationDetail,
  ARMHybridDetail,
  ARMServer,
  RTFApplication,
  ARMLoggingComponent,
  ARMLoggingConfiguration
} from './models'

export default class AppRemoteApi extends RemoteApi {

  constructor(authorization: string) {
    super('', authorization)
  }

  fetchCloudHubApplications(envId: string): Promise<ARMApplicationDetail[]> {
    const headers = {
      'X-ANYPNT-ENV-ID': envId
    }

    return this._get(['cloudhub', 'api', 'v2', 'applications'], true, headers)
  }

  fetchHybridApplications(orgId: string, envId: string, targetId?: string): Promise<{data: ARMHybridDetail[]}> {
    const headers = {
      'X-ANYPNT-ENV-ID': envId,
      'X-ANYPNT-ORG-ID': orgId
    }

    const queryParameters = targetId ? {targetId: targetId} : undefined

    return this._get(['hybrid', 'api', 'v1', 'applications'], true, headers, queryParameters)
  }

  fetchApplicationData(orgId: string, envId: string, appName: string): Promise<ARMApplicationDetail> {
    const headers = {
      'X-ANYPNT-ENV-ID': envId,
      'X-ANYPNT-ORG-ID': orgId,
      'X-CH-SuppressBasicAuth': 1
    }

    const parameters = {retreiveStatistics: true}

    return this._get(['cloudhub', 'api', 'v2', 'applications', appName], true, headers, parameters)
  }

  fetchHybridData(orgId: string, envId: string, id: string): Promise<{ data: ARMHybridDetail }> {
    const headers = {
      'X-ANYPNT-ENV-ID': envId,
      'X-ANYPNT-ORG-ID': orgId,
      'X-CH-SuppressBasicAuth': 1
    }

    const parameters = {
      retreiveStatistics: true
    }

    return this._get(
      ['hybrid', 'api', 'v1', 'applications', id], true, headers, parameters
    )
  }

  fetchRTFData(orgId: string, envId: string, id: string): Promise<RTFApplication> {
    const headers = {
      'X-ANYPNT-ENV-ID': envId,
      'X-ANYPNT-ORG-ID': orgId,
      'X-CH-SuppressBasicAuth': 1
    }

    const parameters = {
      retreiveStatistics: true
    }

    return this._get(
      ['amc', 'application-manager', 'api', 'v2', 'organizations', orgId, 'environments', envId, 'deployments', id], true, headers, parameters
    )
  }

  fetchApplications(orgId: string, envId: string): Promise<{ data: ARMApplication[] }> {
    const headers = {
      'X-ANYPNT-ENV-ID': envId,
      'X-ANYPNT-ORG-ID': orgId,
      'X-CH-Poller': 1,
      'X-CH-SuppressBasicAuth': 1
    }
    return this._get(['armui', 'api', 'v1', 'applications'], true, headers)
  }

  fetchServers(orgId: string, envId: string): Promise<{ data: ARMServer[] }> {
    const headers = {
      'X-ANYPNT-ENV-ID': envId,
      'X-ANYPNT-ORG-ID': orgId,
      'X-CH-Poller': 1,
      'X-CH-SuppressBasicAuth': 1
    }
    return this._get(['armui', 'api', 'v1', 'servers'], true, headers)
  }

  fetchComponents(orgId: string, envId: string, serverId: string): Promise<{ data: ARMComponent[] }> {
    const headers = {
      'X-ANYPNT-ENV-ID': envId,
      'X-ANYPNT-ORG-ID': orgId,
      'X-CH-Poller': 1,
      'X-CH-SuppressBasicAuth': 1
    }

    return this._get(['hybrid', 'api', 'v1', 'targets', serverId, 'components'], true, headers)
  }

  patchSettings(orgId: string, envId: string, serverId: string, component: ARMComponent): Promise<{ data: ARMComponent }> {
    const headers = {
      'X-ANYPNT-ENV-ID': envId,
      'X-ANYPNT-ORG-ID': orgId,
      'X-CH-Poller': 1,
      'X-CH-SuppressBasicAuth': 1
    }

    return this._patch(['hybrid', 'api', 'v1', 'targets', serverId, 'components', component.component.id + ''],
                       {configuration: component.configuration}, true, headers)
  }

  putApplication(orgId: string, app: PutARMApplicationDetail, envId?: string): Promise<ARMApplicationDetail> {
    const headers = {
      'X-ANYPNT-ENV-ID': envId ? envId : '',
      'X-ANYPNT-ORG-ID': orgId,
      'X-CH-SuppressBasicAuth': 1
    }

    const parameters = {
      _: new Date().getTime()
    }

    return this._put(['cloudhub', 'api', 'v2', 'applications', app.domain], {applicationInfo: app}, true, headers, parameters)
  }

  fetchHybridLoggingComponent(orgId: string, envId: string, targetId: string): Promise<{data: ARMLoggingComponent}> {
    const headers = {
      'X-ANYPNT-ORG-ID': orgId,
      'X-ANYPNT-ENV-ID': envId,
    }

    return this._get(['hybrid', 'api', 'v1', 'targets', targetId, 'components', '104'], true, headers)
  }

  updateHybridLoggingComponent(orgId: string, envId: string, targetId: string, conf: ARMLoggingConfiguration): Promise<{data: ARMLoggingComponent}> {
    const headers = {
      'X-ANYPNT-ORG-ID': orgId,
      'X-ANYPNT-ENV-ID': envId,
    }

    const body = {configuration: conf}

    return this._patch(['hybrid', 'api', 'v1', 'targets', targetId, 'components', '104'], body, true, headers)
  }

  fetchRTFApplication(orgId: string, envId: string, id: string): Promise<RTFApplication> {
    const headers = {
      'X-ANYPNT-ENV-ID': envId,
      'X-ANYPNT-ORG-ID': orgId,
      'X-CH-SuppressBasicAuth': 1
    }

    const parameters = {
      _: new Date().getTime()
    }

    return this._get(['amc', 'adam', 'api', 'organizations', orgId, 'environments', envId, 'deployments', id], true, headers, parameters)
  }

  updateRTFApplication(orgId: string, app: RTFApplication, envId: string, id: string): Promise<{data:RTFApplication}> {
    const headers = {
      'X-ANYPNT-ENV-ID': envId,
      'X-ANYPNT-ORG-ID': orgId,
      'X-CH-SuppressBasicAuth': 1
    }

    return this._patch(['amc', 'adam', 'api', 'organizations', orgId, 'environments', envId, 'deployments', id], app, true, headers)
  }
}
